<script setup lang="ts">
import { ref } from "vue";
import type { MenuProps, MenuItemProps, MenuSlots } from ".";
import { useMenu } from "./useMenu";
import MenuFloatingPanel from "./MenuFloatingPanel.vue";

const props = withDefaults(defineProps<MenuProps<MenuItemProps>>(), {
  logo: true,
  externalIcon: "export-outline",
  collapsible: true,
  open: false,
});

defineSlots<MenuSlots>();

const floatingContentRef = ref<HTMLElement | null>(null);
const floatingInnerRef = ref<HTMLElement | null>(null);

const {
  classes,
  collapsed,
  openProxy,
  toggleCollapse,
  toggleOpen,
  hasLeadingSlot,
  hasTrailingSlot,
  showLogo,
  collapsibleEnabled,
  collapsibleButtonProps,
  floatingStyle,
  floatingData,
  floatingVisible,
  isPathActive,
  logoUrl,
  appearanceIcon,
  toggleAppearance,
  floatingPanelClasses,
  handleFloatingNavigate,
} = useMenu({ props, floatingContentRef, floatingInnerRef });
</script>

<template>
  <nav
    part="base"
    :class="classes.base"
    class="e-menu-root"
    role="navigation"
    data-orientation="vertical"
    :data-collapsed="collapsed"
    :data-open="openProxy"
  >
    <div part="menu" :class="classes.menu" class="e-menu">
      <div part="header" :class="classes.header" class="e-menu-header" :data-collapsed="collapsed">
        <slot name="header">
          <a href="/">
            <e-image v-if="showLogo" :src="logoUrl" :class="classes.logo" class="e-menu-logo" />
          </a>

          <div :class="classes.headerOptions" class="e-menu-header-options">
            <e-button variant="ghost" square :icon="appearanceIcon" @click="toggleAppearance"></e-button>
            <e-button variant="ghost" square :icon="openProxy ? 'close' : 'menu'" @click="toggleOpen"></e-button>
          </div>
        </slot>
      </div>
      <!-- part="leading" and part="trailing" are only present when the respective slot has content -->
      <div v-show="hasLeadingSlot" part="leading" :class="classes.leading" class="e-menu-leading">
        <slot name="leading" />
      </div>
      <ul part="list" :class="classes.list" class="e-menu-list">
        <slot>
          <e-menu-item v-for="(item, index) in items" :key="index" v-bind="item" />
        </slot>
      </ul>
      <div v-show="hasTrailingSlot" part="trailing" :class="classes.trailing" class="e-menu-trailing">
        <slot name="trailing" />
      </div>
    </div>

    <div part="footer-wrapper" :class="classes.footerWrapper" class="e-menu-footer-wrapper">
      <div part="footer" :class="classes.footer" class="e-menu-footer">
        <slot name="footer">
          <e-button variant="ghost" square :icon="appearanceIcon" @click="toggleAppearance"></e-button>
        </slot>
      </div>

      <e-button
        v-if="collapsibleEnabled"
        :class="classes.collapsibleButton"
        class="e-menu-footer-collapsible-button"
        variant="ghost"
        icon="collapse"
        square
        v-bind="collapsibleButtonProps"
        @click="toggleCollapse"
      >
      </e-button>
    </div>

    <button
      v-if="collapsibleEnabled"
      type="button"
      :class="classes.backgroundButton"
      class="e-menu-background-button"
      @click="toggleCollapse"
    ></button>

    <!-- Uses v-show so ::part(floating) is always in the DOM — consumers can safely apply transition styles. -->
    <div
      v-show="floatingVisible"
      ref="floatingContentRef"
      part="floating"
      :style="floatingStyle"
      :class="classes.floating"
      class="e-menu-floating"
    >
      <div
        ref="floatingInnerRef"
        class="min-h-full rounded-lg border border-(--color-neutral)/50 bg-(--color-card) shadow-md"
      >
        <menu-floating-panel
          :data="floatingData"
          :classes="floatingPanelClasses"
          :is-path-active="isPathActive"
          @e-menu-navigate="handleFloatingNavigate"
        />
      </div>
    </div>
  </nav>
</template>

<style>
:host {
  display: contents;
}
</style>
